import { Box, Breadcrumbs, Link, Stack, Typography } from '@mui/material';
import React from 'react';

interface SolutionBreadcrumbProps {
  selectedSolution: string | null;
  selectedStage: string | null;
  onResetAll: () => void;
  onResetSolution: () => void;
  variant?: 'default' | 'diagram';
  useCaseData?: {
    name: string;
    useCase?: number;
  } | null;
}

const SolutionBreadcrumb: React.FC<SolutionBreadcrumbProps> = ({
  selectedSolution,
  selectedStage,
  onResetSolution,
  variant = 'default',
  useCaseData,
}) => {
  const getCurrentLevel = () => {
    if (selectedStage) return 'stage';
    if (selectedSolution) return 'solution';
    return null;
  };

  const renderBreadcrumbLinks = () => {
    const items = [];
    const currentLevel = getCurrentLevel();

    // Add solution link if it exists and we're at stage level
    if (selectedSolution && selectedStage) {
      items.push(
        <Link
          key="solution"
          underline="hover"
          color="inherit"
          onClick={onResetSolution}
          sx={{ cursor: 'pointer' }}
        >
          {selectedSolution}
        </Link>
      );
    }

    // Add current level as Typography
    const currentItem = selectedStage || selectedSolution;
    if (currentItem && currentLevel) {
      items.push(
        <Typography key="current" color="text.primary">
          {currentItem}
        </Typography>
      );
    }

    return items;
  };

  // Update the render logic in the diagram variant
  if (variant === 'diagram') {
    const currentLevel = getCurrentLevel();

    const getCurrentHeading = () => {
      if (selectedStage) {
        return selectedStage;
      }
      if (selectedSolution) {
        return selectedSolution;
      }
      return useCaseData?.name || '';
    };

    return (
      <Stack spacing={1} sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: 500,
              fontSize: '1.5rem',
              color: '#626265',
              maxWidth: '460px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {getCurrentHeading()}
          </Typography>
          {useCaseData?.useCase && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              <Typography variant="body2" sx={{ color: '#7c8ea7' }}>
                GEN AI SOLUTION: {useCaseData.useCase}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            color: '#7c8ea7',
            fontSize: '1.5rem',
          }}
        >
          {selectedSolution && currentLevel === 'stage' && (
            <>
              <Link
                component="button"
                underline="hover"
                onClick={onResetSolution}
                sx={{
                  color: '#7c8ea7',
                  fontSize: '1.5rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {selectedSolution}
              </Link>
              {selectedStage && ' / '}
            </>
          )}

          {selectedStage && (
            <Typography
              sx={{
                color: '#7c8ea7',
                fontSize: '1.5rem',
              }}
            >
              {selectedStage}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  }

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ m: 1 }}>
      {renderBreadcrumbLinks()}
    </Breadcrumbs>
  );
};

export default SolutionBreadcrumb;
